import type { StoryResponse } from '@manasil/shared';
import { STORY_BANNER } from '@manasil/shared';
import { useEffect, useState } from 'react';
import { resolveMediaUrl } from '../lib/api.js';
import {
  BANNER_SPEC,
  IMAGE_ACCEPT,
  type ImageCheck,
  inspectImage,
  removeStoryBanner,
  uploadStoryBanner,
} from '../lib/image.js';
import { usePlayer } from '../lib/player.js';
import {
  ACCEPT_ATTRIBUTE,
  normaliseContentType,
  probeDuration,
  uploadStoryAudio,
} from '../lib/upload.js';
import { errorMessage, formatDuration } from './ui.js';

function formatBytes(bytes: number): string {
  return bytes >= 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

type BannerChoice =
  | { kind: 'keep' }
  | { kind: 'replace'; file: File; check: ImageCheck }
  | { kind: 'remove' };

/**
 * The audio and the banner of one story, as a form.
 *
 * A day's story and an episode's story are the same thing with a different parent, so
 * the two edit forms share this. Nothing is written until `apply` — choosing or clearing
 * a file changes nothing on its own, so a modal's Cancel genuinely cancels. The chosen
 * audio is decoded locally first, which validates it and gives a real duration to show.
 */
export function useStoryMedia(story: StoryResponse | null, onError: (message: string) => void) {
  const [file, setFile] = useState<File | null>(null);
  const [fileDuration, setFileDuration] = useState<number | null>(null);
  const [banner, setBanner] = useState<BannerChoice>({ kind: 'keep' });

  // An object URL is a live handle to the file; letting them pile up leaks the file itself.
  useEffect(() => {
    if (banner.kind !== 'replace') return;
    const url = banner.check.previewUrl;
    return () => URL.revokeObjectURL(url);
  }, [banner]);

  async function chooseFile(event: React.ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;

    if (!normaliseContentType(chosen)) {
      onError(`Manasil takes MP3, M4A, AAC or WAV audio — "${chosen.name}" is not one of those.`);
      return;
    }
    try {
      // Decoding here means an unreadable file is caught before anything is uploaded.
      setFileDuration(await probeDuration(chosen));
      setFile(chosen);
    } catch (caught) {
      onError(errorMessage(caught));
    }
  }

  async function chooseBanner(event: React.ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    try {
      setBanner({ kind: 'replace', file: chosen, check: await inspectImage(chosen, BANNER_SPEC) });
    } catch (caught) {
      onError(errorMessage(caught));
    }
  }

  function clearFile() {
    setFile(null);
    setFileDuration(null);
  }

  /** Uploads whatever was chosen. Returns true when something about the story changed. */
  async function apply(
    storyId: string,
    setProgress: (fraction: number | null) => void,
  ): Promise<boolean> {
    let touched = false;
    if (file) {
      setProgress(0);
      await uploadStoryAudio(storyId, file, setProgress);
      touched = true;
    }
    if (banner.kind === 'replace') {
      setProgress(0);
      await uploadStoryBanner(storyId, banner.file, banner.check, setProgress);
      touched = true;
    }
    if (banner.kind === 'remove') {
      await removeStoryBanner(storyId);
      touched = true;
    }
    return touched;
  }

  return {
    file,
    fileDuration,
    banner,
    setBanner,
    chooseFile,
    chooseBanner,
    clearFile,
    apply,
    dirty: file !== null || banner.kind !== 'keep',
    /** A banner that failed its checks blocks saving until it is swapped or undone. */
    blocked: banner.kind === 'replace' && banner.check.problems.length > 0,
    story,
  };
}

export type StoryMedia = ReturnType<typeof useStoryMedia>;

export function StoryMediaFields({
  media,
  saving,
  progress,
  playContext,
  emptyHint,
}: {
  media: StoryMedia;
  saving: boolean;
  progress: number | null;
  /** What the docked player shows as context, e.g. "Day 3" or "Kural 391". */
  playContext: string;
  /** Why the audio matters here — what cannot happen without it. */
  emptyHint: string;
}) {
  const player = usePlayer();
  const { story, file, fileDuration, banner } = media;

  /**
   * A blob: URL for a file not yet uploaded, otherwise the stored banner — which arrives
   * as a RELATIVE signed path from the local storage driver and would otherwise resolve
   * against the portal's own origin rather than the API's.
   */
  const shownBanner =
    banner.kind === 'replace'
      ? banner.check.previewUrl
      : banner.kind === 'remove' || !story?.bannerUrl
        ? null
        : resolveMediaUrl(story.bannerUrl);

  return (
    <>
      <div className="field">
        <span className="label">Audio</span>

        <div className="audio-box">
          {file ? (
            <>
              <div className="audio-info">
                <span className="pill pill-missing">New</span>
                <div className="grow">
                  <div className="audio-name truncate">{file.name}</div>
                  <div className="hint">
                    {fileDuration !== null ? formatDuration(fileDuration) : '—'} ·{' '}
                    {formatBytes(file.size)} · replaces the current audio when you save
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={media.clearFile}
                disabled={saving}
              >
                Remove
              </button>
            </>
          ) : story?.hasAudio ? (
            <>
              <div className="audio-info">
                <button
                  type="button"
                  className="day-play"
                  onClick={() =>
                    void player.play({
                      storyId: story.id,
                      title: story.title,
                      context: playContext,
                    })
                  }
                  aria-label="Play this story"
                >
                  ▶
                </button>
                <div className="grow">
                  <div className="audio-name">{formatDuration(story.durationSec)}</div>
                  <div className="hint">{formatBytes(story.sizeBytes)} · uploaded</div>
                </div>
              </div>
              <label className="btn btn-sm file-btn">
                Replace
                <input type="file" accept={ACCEPT_ATTRIBUTE} hidden onChange={media.chooseFile} />
              </label>
            </>
          ) : (
            <>
              <div className="audio-info">
                <span className="pill pill-missing">No audio</span>
                <span className="hint">{emptyHint}</span>
              </div>
              <label className="btn btn-sm btn-primary file-btn">
                Choose file
                <input type="file" accept={ACCEPT_ATTRIBUTE} hidden onChange={media.chooseFile} />
              </label>
            </>
          )}
        </div>

        {progress !== null ? (
          <div className="upload-row">
            <div className="bar">
              <div className="bar-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
            <span className="upload-pct nums">{Math.round(progress * 100)}%</span>
          </div>
        ) : null}

        <span className="hint">MP3, M4A, AAC or WAV · up to 50 MB</span>
      </div>

      <div className="field">
        <span className="label">Banner</span>

        <div className="audio-box">
          {/* The app's own crop and scrim, to scale: the player darkens from 55% down, so
              a subject in the lower half is buried behind the title and controls. */}
          <div className="banner-thumb">
            {shownBanner ? (
              <>
                <img src={shownBanner} alt="" />
                <div className="banner-scrim" />
              </>
            ) : (
              <span className="banner-empty">No art</span>
            )}
          </div>

          <div className="grow">
            {banner.kind === 'replace' ? (
              <>
                <div className="audio-name truncate">{banner.file.name}</div>
                <div className="hint nums">
                  {banner.check.width} × {banner.check.height} ·{' '}
                  {Math.round(banner.file.size / 1024)} KB · applied when you save
                </div>
              </>
            ) : banner.kind === 'remove' ? (
              <div className="hint">Removed when you save.</div>
            ) : story?.hasBanner ? (
              <div className="hint nums">
                {story.bannerWidth} × {story.bannerHeight} · uploaded
              </div>
            ) : (
              <div className="hint">
                Optional — the app falls back to the goal's colour and emoji.
              </div>
            )}
          </div>

          {banner.kind === 'keep' && story?.hasBanner ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => media.setBanner({ kind: 'remove' })}
              disabled={saving}
            >
              Remove
            </button>
          ) : banner.kind !== 'keep' ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => media.setBanner({ kind: 'keep' })}
              disabled={saving}
            >
              Undo
            </button>
          ) : null}

          <label className={`btn btn-sm file-btn${story?.hasBanner ? '' : ' btn-primary'}`}>
            {story?.hasBanner || banner.kind === 'replace' ? 'Replace' : 'Choose image'}
            <input type="file" accept={IMAGE_ACCEPT} hidden onChange={media.chooseBanner} />
          </label>
        </div>

        {banner.kind === 'replace'
          ? banner.check.problems.map((problem) => (
              <div key={problem} className="banner">
                {problem}
              </div>
            ))
          : null}
        {banner.kind === 'replace'
          ? banner.check.warnings.map((warning) => (
              <span key={warning} className="hint">
                {warning}
              </span>
            ))
          : null}

        <span className="hint">
          {STORY_BANNER.width} × {STORY_BANNER.height} square · JPEG · keep the subject in the top
          half, the player darkens the bottom.
        </span>
      </div>
    </>
  );
}
